/* global process */
import { retrieveValue } from 'helpers/i18n';
import { ErrorTypes, ErrorCategory } from 'helpers/Analytics/Enums';
import appStorage from 'helpers/AppStorage/appStorage.helper';

export default class Analytics {
    constructor() {
        this.persistentCustom = {
            version: 'Checkout ' + process.env.REACT_APP_VERSION,
        };
        this.ecommerceParams = {};
    }

    logEvent(category, action, label, custom = {}) {
        if (window.Checkout.settings && window.Checkout.settings.enable_analytics && typeof action === 'string') {
            const options = {};

            options.event_action = action;
            if (category) options.event_category = category;
            if (label) options.event_label = label;
            options.custom = Object.assign({}, this.persistentCustom, custom);

            if (appStorage('debug')) console.log('DEBUG MODE: logEvent() is sending window.sendEvent(options)', options); // eslint-disable-line no-console

            if (!window.sendEvent) return false;
            window.sendEvent(options);
            return true;
        }
        return false;
    }

    logPageView() {
        if (window.Checkout.settings && window.Checkout.settings.enable_analytics && window.sendEvent) {
            window.sendEvent({
                event_category: 'pageview',
                page_path: window.location.pathname,
                page_location: window.location.href,
            });
        }
    }

    logEcommerceEvent({ name, label = '', params = {}, custom = {} }) {
        if (window.Checkout.settings && window.Checkout.settings.enable_analytics && name) {
            const options = {};
            const customParams = Object.assign({}, this.ecommerceParams, params);

            options.name = name;
            if (Object.keys(customParams).length > 0) options.params = customParams;
            options.custom = Object.assign({}, this.persistentCustom, custom);

            // Special case - set_checkout_option shouldn’t send items
            if (name === 'set_checkout_option') delete options.params.items;

            if (label) options.event_label = label;

            if (appStorage('debug')) console.log('DEBUG MODE: logEcommerceEvent() is sending window.sendEvent(options)', options); // eslint-disable-line no-console

            if (!window.sendEvent) return false;

            window.sendEvent(options);
            return true;
        }
        return false;
    }

    setPersistentCustom(key, value) {
        if (value === null) {
            delete this.persistentCustom[key];
        } else {
            this.persistentCustom[key] = value;
        }
    }

    // Analytics wants a product identifier in the form "product_name product_id"
    // for example "ARKELSTORP 30260807"
    productId(product) {
        return product.productName + ' ' + product.articleNumber;
    }

    productsData(products, assemblyServiceProducts, eventName) {
        return Object.keys(products).map(productId => {
            const item = {};
            const product = products[productId];

            if (product.articleNumber) item.id = product.articleNumber.toString().replace('S', '');
            if (product.quantity) item.quantity = product.quantity;
            if (product?.regularPrice?.exclTax) item.price = product?.regularPrice?.exclTax || 0;

            // add assembly service product parameters (if any)
            if (assemblyServiceProducts) {
                const productIsEligibleForAssembly = Object.keys(assemblyServiceProducts).includes(productId);

                if (productIsEligibleForAssembly) {
                    const assemblyServiceProduct = assemblyServiceProducts[productId];

                    item.assembly_available = true;
                    item.assembly_selected = assemblyServiceProduct.isAssemblySelected;

                    // add these specifically for the 'purchase' event
                    if (eventName === 'purchase') {
                        item.assembly_price = assemblyServiceProduct.unformattedItemAssemblyPrice;
                        item.assembly_purchased_number_of_times = 1;
                    }
                }
            }

            return item;
        });
    }

    // Number of days between current date and offered timeslot
    dayDelta(dateString) {
        return Math.ceil((new Date(dateString) - new Date()) / 1000 / 60 / 60 / 24);
    }

    logInputValidationError(fieldName, errorKey, errorCause) {
        window.Checkout.analytics.logEvent('error', 'checkout', errorKey, {
            error_code: fieldName,
            error_id: errorKey,
            error_text: retrieveValue(window.Checkout.text, errorKey) || '',
            error_type: ErrorTypes.FrontendValidation,
            error_category: ErrorCategory.UserError,
            error_cause: errorCause,
        });
    }
}
